import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { User } from '../api/entities.js';
import { 
  ArrowLeft, 
  User as UserIcon,
  Mail,
  Building2,
  Shield,
  Calendar,
  Clock,
  Edit,
  AlertCircle,
  Phone
} from 'lucide-react';
import { Button } from "../components/ui/button.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.jsx";
import { Badge } from "../components/ui/badge.jsx";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function ViewUser() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Obter ID do usuário da URL
      const urlParams = new URLSearchParams(window.location.search);
      const userId = urlParams.get('id');
      
      if (!userId) {
        navigate(createPageUrl('Users'));
        return;
      }

      // Carregar dados do usuário atual para verificar permissões
      const currentUserData = await User.me();
      setCurrentUser(currentUserData);

      // Verificar permissões - apenas usuários internos podem ver detalhes de outros usuários
      if (currentUserData.role === 'client' && currentUserData.id !== userId) {
        navigate(createPageUrl('Dashboard'));
        return;
      }

      // Buscar dados reais do usuário via API
      console.log('🔍 Buscando usuário:', userId);
      const userDataResult = await User.findById(userId);
      
      if (!userDataResult) {
        setError("Usuário não encontrado");
        return;
      }
      
      console.log('✅ Usuário encontrado:', userDataResult);
      setUserData(userDataResult);

    } catch (error) {
      console.error('Error loading user data:', error);
      setError("Erro ao carregar dados do usuário: " + error.message);
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-gray-200 rounded animate-pulse" />
        <div className="h-32 bg-gray-200 rounded animate-pulse" />
      </div>
    );
  }

  if (error || !userData) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold">Erro ao Carregar Usuário</h2>
        <p className="text-muted-foreground mt-2">
          {error || "Usuário não encontrado ou foi removido."}
        </p>
        <Button 
          onClick={() => navigate(createPageUrl('Users'))}
          className="mt-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar para Usuários
        </Button>
      </div>
    );
  }

  const isAdmin = currentUser?.role === 'admin';
  const isSameUser = currentUser?.id === userData.id;
  const canEdit = isAdmin || isSameUser;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(createPageUrl('Users'))}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{userData.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={`
                ${userData.role === 'admin' ? 'bg-purple-100 text-purple-800' : 
                  userData.role === 'support' ? 'bg-blue-100 text-blue-800' : 
                  'bg-gray-100 text-gray-800'}
              `}>
                {userData.role === 'admin' ? 'Administrador' : 
                 userData.role === 'support' ? 'Suporte' : 
                 userData.role === 'client' ? 'Cliente' : 
                 userData.role}
              </Badge>
              <span className="text-muted-foreground">{userData.email}</span>
            </div>
          </div>
        </div>

        {canEdit && (
          <Button 
            onClick={() => navigate(createPageUrl(`EditUser?id=${userData.id}`))}
          >
            <Edit className="mr-2 h-4 w-4" />
            Editar Usuário
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Perfil</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col items-center">
              <Avatar className="h-24 w-24 mb-4">
                <AvatarImage src={userData.avatar_url} alt={userData.name} />
                <AvatarFallback className="text-xl">
                  {userData.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                </AvatarFallback>
              </Avatar>
              
              <h2 className="text-xl font-bold text-center">{userData.name}</h2>
              <p className="text-muted-foreground text-center">{userData.email}</p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Shield className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Função:</span>
                <span className="ml-auto font-medium">
                  {userData.role === 'admin' ? 'Administrador' :
                   userData.role === 'support' ? 'Suporte' :
                   userData.role === 'client' ? 'Cliente' :
                   userData.role}
                </span>
              </div>

              {userData.phone && (
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Telefone:</span>
                  <span className="ml-auto font-medium">{userData.phone}</span>
                </div>
              )}

              {userData.companies && (
                <div className="flex items-center gap-3 text-sm">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Empresa:</span>
                  <span className="ml-auto font-medium">{userData.companies.name}</span>
                </div>
              )}

              <div className="flex items-center gap-3 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Registro:</span>
                <span className="ml-auto font-medium">
                  {userData.created_at && !isNaN(new Date(userData.created_at)) ? format(new Date(userData.created_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR }) : 'Data não disponível'}
                </span>
              </div>

              <div className="flex items-center gap-3 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Admin:</span>
                <span className="ml-auto font-medium">{userData.is_admin ? 'Sim' : 'Não'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Outras seções do perfil */}
        <div className="md:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Atividades Recentes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma atividade recente registrada</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}